import React, { useState } from 'react';
import { PayablesResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import PayablesSimulation from '../simulations/PayablesSimulation';
import { modelStories } from '../../data/stories';
import { DollarSign, Calendar, TrendingUp } from 'lucide-react';

interface PayablesModuleProps {
  data: PayablesResult | null;
  onOptimizationComplete: (data: PayablesResult) => void;
  onLoadingStart: () => void;
}

const PayablesModule: React.FC<PayablesModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizePayables();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Payables Optimization</h2>
        <p className="text-gray-600 mb-4">
          Optimize payment timing using Dynamic Programming
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.payables} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleOptimize} className="btn-primary">Optimize Payment Schedule</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Total Savings</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <DollarSign className="w-5 h-5 mr-1" />
                {formatCurrency(data.savings)}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <div className="text-2xl font-bold text-green-600">{data.status}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Payments Optimized</div>
              <div className="text-2xl font-bold text-gray-800">{data.payments.length}</div>
            </div>
          </div>

          {/* Payment Schedule Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Optimized Payment Schedule</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recommended</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.payments.map((payment, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.vendor}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(payment.amount)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                        {payment.due}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-blue-600">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {payment.recommended}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      {payment.discount > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {formatCurrency(payment.discount)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Payment Strategy Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <PayablesSimulation />
      )}
    </div>
  );
};

export default PayablesModule;
